// src/api/chatApi.js

function getAuthToken() {
  // Prefer your existing auth utility/context if you have one.
  // This fallback covers common patterns used in admin apps.
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

async function safeText(res) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

async function apiFetch(path, { method = "GET", body, signal } = {}) {
  const token = getAuthToken();

  const res = await fetch(path, {
    method,
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // Normalize common error cases
  if (res.status === 401) {
    const text = await safeText(res);
    const err = new Error(text || "Unauthorized (token expired).");
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    const text = await safeText(res);
    const err = new Error(text || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  // 204 No Content (common for DELETE)
  if (res.status === 204) return null;

  // Some endpoints may return 200 with empty body, or non-JSON payloads.
  const contentType = res.headers.get("content-type") || "";
  const raw = await res.text();
  if (!raw) return null;

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(raw);
    } catch {
      // If server mislabels/returns invalid JSON, return raw to avoid hard crash.
      return raw;
    }
  }

  // Fallback: return text payload if not JSON
  return raw;
}

/* -----------------------------
   Chat API helpers (Aegis Admin)
   ----------------------------- */

export async function createNewChat({ surface, signal } = {}) {
  // POST /api/chat/new
  // Optional body: { surface }  ("chat" | "chat2") — tags the session's module.
  return apiFetch("/api/chat/new", {
    method: "POST",
    body: surface ? { surface } : undefined,
    signal,
  });
}

export async function listChatSessions({ limit = 50, surface, signal } = {}) {
  // GET /api/chat/sessions?limit=50&surface=chat
  const qs = new URLSearchParams({ limit: String(limit) });
  if (surface) qs.set("surface", surface);
  return apiFetch(`/api/chat/sessions?${qs.toString()}`, { signal });
}

export async function loadChatSession(sessionId, { signal } = {}) {
  // GET /api/chat/session/<sessionId>
  return apiFetch(`/api/chat/session/${encodeURIComponent(sessionId)}`, { signal });
}

export async function renameChatSession(sessionId, title, { signal } = {}) {
  // PATCH /api/chat/session/<sessionId>
  // Body: { title }
  return apiFetch(`/api/chat/session/${encodeURIComponent(sessionId)}`, {
    method: "PATCH",
    body: { title },
    signal,
  });
}

export async function askChat(sessionId, message, { signal } = {}) {
  // POST /api/chat/ask
  // Body: { sessionId, message }
  return apiFetch("/api/chat/ask", {
    method: "POST",
    body: { sessionId, message },
    signal,
  });
}

export async function sendFeedback(
  { responseId, question, sql, source, sessionId, rating },
  { signal } = {}
) {
  // POST /api/feedback
  // rating: "up" | "down". Prefer responseId (the assistant message id); include
  // { question, sql, source } as a fallback so the exchange is identifiable even
  // when no id is available. sessionId is passed through for traceability.
  return apiFetch("/api/feedback", {
    method: "POST",
    body: { responseId, question, sql, source, sessionId, rating },
    signal,
  });
}

export async function deleteChatSession(sessionId, { signal } = {}) {
  // DELETE /api/chat/session/<sessionId>
  // Typically returns 204 No Content; apiFetch returns null in that case.
  return apiFetch(`/api/chat/session/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
    signal,
  });
}

